import React, { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { produtoGeometria } from '../lib/geometriaProduto';

// Peça de compósito em 3D (18/09/2026): para-choque, grade, teto, para-lama
// ou painel, pela família do produto e na cor do cliente — three.js, chunk
// separado: só baixa quando a tela de Produção mostra o produto.
const Peca3DReal = lazy(() => import('./peca3d/Peca3D'));

// Pedido do usuário (18/09/2026): a apresentação mostra UMA peça, o
// para-choque cinza do VW Delivery da página de produtos do site — em toda OP.
// Para-choque do VW Delivery desenhado em 3D (18/09/2026): o usuário pediu a
// peça reta, sem a distorção da foto. Cinza fosco como a peça sai da prensa.
const PARA_CHOQUE_VW_DELIVERY = {
  familia: 'para-choque',
  medidas: { c: 2150, l: 320, a: 380 },
  cor: '#8e9196',
  foto: { caminho: `${import.meta.env.BASE_URL}pecas/para-choque-vw-delivery.png` },
  modelo: `${import.meta.env.BASE_URL}pecas/para-choque-vw-delivery.glb`,
};

export function Vista3DProduto() {
  const { familia, medidas, cor, foto, modelo } = PARA_CHOQUE_VW_DELIVERY;
  const [temModelo, setTemModelo] = useState(false);
  const aoTerModelo = useCallback((tem) => setTemModelo(tem), []);
  const download = temModelo
    ? { href: modelo, nome: 'para-choque-vw-delivery.glb', rotulo: 'Baixar 3D' }
    : { href: foto.caminho, nome: 'para-choque-vw-delivery.png', rotulo: 'Baixar imagem' };
  return (
    <div className="absolute inset-0 caixa3d-palco select-none">
      <Suspense fallback={<div className="absolute inset-0 grid place-items-center text-sm caixa3d-aviso">Montando a peça…</div>}>
        <Peca3DReal familia={familia} medidas={medidas} cor={cor} foto={foto} modelo={modelo} aoTerModelo={aoTerModelo} />
      </Suspense>
      <a
        href={download.href}
        download={download.nome}
        onClick={(e) => e.stopPropagation()}
        className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold text-white shadow-md hover:brightness-110 transition"
        style={{ background: 'var(--grad-laranja)' }}
      >
        <Download size={13} /> {download.rotulo}
      </a>
      <div className="absolute bottom-2 left-2 px-2.5 py-1 rounded-lg caixa3d-legenda text-[10px] font-semibold tabular-nums pointer-events-none">
        SMC · Para-choque VW Delivery · {medidas.c} × {medidas.l} × {medidas.a} mm
      </div>
    </div>
  );
}

const C = {
  fundo: '#0c1420',
  grade: '#1c2b42',
  linha: '#9fd7f7',
  cota: '#f6b34c',
  texto: '#b6c2d9',
  destaque: '#8be0c8',
};

function CotaH({ x1, x2, y, texto, dir = 1 }) {
  const yl = y + 16 * dir;
  const yTexto = dir === 1 ? yl - 5 : yl + 14;
  return (
    <g>
      <line x1={x1} y1={y} x2={x1} y2={yl} stroke={C.cota} strokeWidth={0.8} />
      <line x1={x2} y1={y} x2={x2} y2={yl} stroke={C.cota} strokeWidth={0.8} />
      <line x1={x1 + 8} y1={yl} x2={x2 - 8} y2={yl} stroke={C.cota} strokeWidth={1} markerStart="url(#seta-i)" markerEnd="url(#seta-f)" />
      <text x={(x1 + x2) / 2} y={yTexto} fill={C.cota} fontSize={10} fontWeight={600} textAnchor="middle">{texto}</text>
    </g>
  );
}

function CotaV({ x, y1, y2, texto, dir = 1 }) {
  const xl = x + 16 * dir;
  const xTexto = xl + 5 * dir;
  return (
    <g>
      <line x1={x} y1={y1} x2={xl} y2={y1} stroke={C.cota} strokeWidth={0.8} />
      <line x1={x} y1={y2} x2={xl} y2={y2} stroke={C.cota} strokeWidth={0.8} />
      <line x1={xl} y1={y1 + 8} x2={xl} y2={y2 - 8} stroke={C.cota} strokeWidth={1} markerStart="url(#seta-i)" markerEnd="url(#seta-f)" />
      <text x={xTexto} y={(y1 + y2) / 2 + 3} fill={C.cota} fontSize={10} fontWeight={600} textAnchor={dir === 1 ? 'start' : 'end'}>{texto}</text>
    </g>
  );
}

const Y = 74;
const GAP = 34;

export function DesenhoTecnicoProduto({ codigo, descricao }) {
  const geo = useMemo(() => produtoGeometria(codigo, descricao), [codigo, descricao]);

  let L;
  let W;
  let H;
  let s;
  const rolo = false;
  if (rolo) {
    s = Math.min(430 / (geo.comprimento + geo.diametro), 240 / geo.diametro, 0.95);
    L = geo.comprimento * s;
    H = geo.diametro * s;
    W = H;
  } else {
    s = Math.min(440 / (geo.largura + geo.profundidade + 60), 300 / (geo.profundidade + geo.altura + 60), 1);
    L = geo.largura * s;
    W = geo.profundidade * s;
    H = Math.max(geo.altura * s, 4);
  }

  const topo = { x: 70, y: Y, w: L, h: W };
  const frente = { x: 70, y: Y + W + GAP, w: L, h: H };
  const lateral = { x: 70 + L + 74, y: Y + GAP, w: W, h: H };

  const titulo = (geo.descricao || '').toUpperCase().slice(0, 32);
  const dimensoes = rolo
    ? `${geo.diametro} mm (Ø) × ${geo.comprimento} mm`
    : `${geo.largura} × ${geo.profundidade} × ${geo.altura} mm`;
  const tipo = `${geo.tipo.toUpperCase()} · ${geo.processo}`;

  return (
    <svg viewBox="0 0 680 420" className="w-full h-full" role="img" aria-label={`Desenho técnico ${geo.codigo}`}>
      <defs>
        <pattern id="grade-tec" width="18" height="18" patternUnits="userSpaceOnUse">
          <line x1="0" y1="4" x2="18" y2="4" stroke={C.grade} strokeWidth="0.5" />
          <line x1="4" y1="0" x2="4" y2="18" stroke={C.grade} strokeWidth="0.5" />
        </pattern>
        <marker id="seta-f" markerWidth="9" markerHeight="9" refX="7" refY="3" orient="auto" markerUnits="strokeWidth">
          <path d="M0,0 L6,3 L0,6 Z" fill={C.cota} />
        </marker>
        <marker id="seta-i" markerWidth="9" markerHeight="9" refX="2" refY="3" orient="auto" markerUnits="strokeWidth">
          <path d="M6,0 L0,3 L6,6 Z" fill={C.cota} />
        </marker>
      </defs>
      <rect width="680" height="420" fill={C.fundo} />
      <rect width="680" height="420" fill="url(#grade-tec)" />

      <text x={topo.x + L / 2} y={topo.y - 12} fill={C.texto} fontSize={9} letterSpacing={2} textAnchor="middle">VISTA SUPERIOR</text>
      <text x={frente.x + L / 2} y={frente.y + H + 30} fill={C.texto} fontSize={9} letterSpacing={2} textAnchor="middle">VISTA FRONTAL</text>
      <text x={lateral.x + lateral.w / 2} y={lateral.y - 12} fill={C.texto} fontSize={9} letterSpacing={2} textAnchor="middle">VISTA LATERAL</text>

      <rect x={topo.x} y={topo.y} width={L} height={W} fill={C.face} fillOpacity={0.12} stroke={C.linha} strokeWidth={1.2} />
      <rect x={lateral.x} y={lateral.y} width={lateral.w} height={H} fill={C.face} fillOpacity={0.12} stroke={C.linha} strokeWidth={1.2} />

      {!rolo && (
        <>
          <path d={`M${frente.x},${frente.y} L${frente.x + L},${frente.y + H} M${frente.x},${frente.y + H} L${frente.x + L},${frente.y}`} stroke={C.linha} strokeWidth={0.6} strokeDasharray="4 4" fill="none" opacity={0.5} />
          {[1, 2, 3, 4].map((i) => (
            <line key={i} x1={frente.x + (L / 5) * i} y1={frente.y} x2={frente.x + (L / 5) * i} y2={frente.y + H} stroke={C.linha} strokeWidth={0.6} strokeDasharray="2 4" opacity={0.45} />
          ))}
          <rect x={frente.x + L / 4} y={frente.y + H / 3} width={L / 2} height={H / 3} fill="none" stroke={C.linha} strokeWidth={0.6} strokeDasharray="3 3" opacity={0.6} />
          <text x={frente.x + L / 2} y={frente.y + H / 2 + 3} fill={C.texto} fontSize={9} fontWeight={700} textAnchor="middle" letterSpacing={1}>{geo.codigo}</text>
          <line x1={lateral.x + lateral.w / 2} y1={lateral.y} x2={lateral.x + lateral.w / 2} y2={lateral.y + H} stroke={C.linha} strokeWidth={0.6} strokeDasharray="4 4" opacity={0.5} />
        </>
      )}

      {rolo && (
        <>
          <line x1={topo.x} y1={topo.y + W / 2} x2={topo.x + L} y2={topo.y + W / 2} stroke={C.linha} strokeWidth={0.6} strokeDasharray="6 4" opacity={0.5} />
          <line x1={frente.x} y1={frente.y + H / 2} x2={frente.x + L} y2={frente.y + H / 2} stroke={C.linha} strokeWidth={0.6} strokeDasharray="6 4" opacity={0.5} />
          <circle cx={lateral.x + lateral.w / 2} cy={lateral.y + H / 2} r={Math.max(H / 2 - 1, 2)} fill={C.face} fillOpacity={0.12} stroke={C.linha} strokeWidth={1.2} />
          <line x1={lateral.x + 3} y1={lateral.y + H / 2} x2={lateral.x + lateral.w - 3} y2={lateral.y + H / 2} stroke={C.cota} strokeWidth={1} markerStart="url(#seta-i)" markerEnd="url(#seta-f)" />
          <text x={lateral.x + lateral.w / 2} y={lateral.y + H / 2 - 8} fill={C.cota} fontSize={10} fontWeight={600} textAnchor="middle">{`Ø ${geo.diametro}`}</text>
        </>
      )}

      {rolo ? (
        <>
          <CotaH x1={70} x2={70 + L} y={topo.y + topo.h} texto={`${geo.comprimento}`} />
          <CotaH x1={70} x2={70 + L} y={frente.y + H} texto={`${geo.comprimento}`} />
          <CotaH x1={lateral.x} x2={lateral.x + lateral.w} y={lateral.y + H} texto={`${geo.diametro}`} />
        </>
      ) : (
        <>
          <CotaH x1={70} x2={70 + L} y={topo.y + topo.h} texto={`${geo.largura}`} />
          <CotaV x={70} y1={topo.y} y2={topo.y + topo.h} texto={`${geo.profundidade}`} dir={-1} />
          <CotaH x1={70} x2={70 + L} y={frente.y + H} texto={`${geo.largura}`} />
          <CotaV x={70} y1={frente.y} y2={frente.y + H} texto={`${geo.altura}`} dir={-1} />
        </>
      )}

      <g>
        <rect x="428" y="330" width="224" height="76" fill="#0e1826" stroke={C.cota} strokeWidth="0.8" />
        <line x1="428" y1="352" x2="652" y2="352" stroke={C.texto} strokeWidth="0.6" opacity="0.5" />
        <line x1="428" y1="366" x2="652" y2="366" stroke={C.texto} strokeWidth="0.6" opacity="0.5" />
        <text x="436" y="345" fill={C.cota} fontSize="10" fontWeight="700" letterSpacing="1">DESENHO TÉCNICO</text>
        <text x="436" y="364" fill={C.texto} fontSize="9">{titulo || geo.codigo}</text>
        <text x="436" y="378" fill={C.destaque} fontSize="9" fontWeight="600">{geo.codigo}</text>
        <text x="436" y="392" fill={C.texto} fontSize="8">{dimensoes} · {tipo}</text>
        <text x="436" y="404" fill={C.texto} fontSize="8">TECNOFIBRAS · ENG. PRODUTO 2026</text>
        <text x="640" y="404" fill={C.texto} fontSize="8" textAnchor="end">{`ESC 1:${Math.max(1, Math.round(1 / s))}`}</text>
      </g>
    </svg>
  );
}